import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth, hasRole } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Copy, CheckCircle2, Lock, Sparkles, Star, Layers } from "lucide-react";
import { toast } from "sonner";

export default function FrameworkDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [fw, setFw] = useState(null);
  const isAdmin = hasRole(user, "tenant_admin", "hr_admin");

  useEffect(() => {
    api.get(`/frameworks/${id}`).then((r) => setFw(r.data));
  }, [id]);

  const clone = async () => {
    try {
      const r = await api.post(`/frameworks/${id}/clone`);
      toast.success("Framework cloned");
      navigate(`/frameworks/${r.data.id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    }
  };

  if (!fw) return <div className="text-sm text-slate-500">Loading…</div>;

  return (
    <div className="max-w-5xl space-y-6" data-testid="framework-detail-page">
      <Link to="/frameworks" className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1.5">
        <ChevronLeft className="w-3 h-3" /> Back to library
      </Link>

      <div className="axia-card p-6 lg:p-8">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-md bg-slate-900 text-white flex items-center justify-center">
              <Layers className="w-5 h-5" />
            </div>
            <div>
              <span
                className={`axia-pill ${
                  fw.status === "published"
                    ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                    : "bg-amber-50 text-amber-700 border-amber-200"
                }`}
              >
                {fw.status === "published" ? <CheckCircle2 className="w-3 h-3" /> : null}
                {fw.status}
              </span>
              <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 mt-1.5">{fw.name}</h1>
              <p className="text-sm text-slate-500 mt-2 max-w-xl">{fw.description}</p>
            </div>
          </div>
          {isAdmin && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={clone} data-testid="framework-clone-btn">
                <Copy className="w-3.5 h-3.5 mr-2" /> Clone to edit
              </Button>
              {fw.status !== "published" && (
                <Button onClick={() => navigate(`/frameworks/${id}/edit`)} data-testid="framework-edit-btn">
                  Edit framework
                </Button>
              )}
            </div>
          )}
        </div>
      </div>

      <Section title="Objective fields" subtitle="Fields employees fill in for each objective">
        <div className="space-y-2">
          {(fw.objective_fields || []).map((f) => (
            <div key={f.id} className="border border-slate-200 rounded-md p-3 bg-white">
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm text-slate-900">{f.label}</span>
                <span className="font-mono text-[10px] text-slate-500 uppercase tracking-wider">{f.type}{f.required ? " · required" : ""}</span>
              </div>
              {f.help_text && <div className="text-xs text-slate-500 mt-1">{f.help_text}</div>}
            </div>
          ))}
        </div>
      </Section>

      {fw.classification?.enabled && (
        <Section title="Classification" subtitle={fw.classification.dimension_name}>
          <div className="flex flex-wrap gap-2">
            {fw.classification.options.map((o) => (
              <span
                key={o.label}
                className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium border"
                style={{ borderColor: o.color, color: o.color, background: `${o.color}10` }}
              >
                {o.label}
              </span>
            ))}
          </div>
        </Section>
      )}

      <Section title="Rating scale" subtitle={`${fw.rating_scale.type} · ${fw.rating_scale.levels.length} levels`}>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {fw.rating_scale.levels.map((l) => (
            <div key={l.value} className="border border-slate-200 rounded-md p-3 text-center">
              <div className="font-mono text-2xl font-bold text-slate-900">{l.value}</div>
              <div className="text-xs font-semibold text-slate-700 mt-1">{l.label}</div>
              {l.description && <div className="text-[11px] text-slate-500 mt-0.5">{l.description}</div>}
            </div>
          ))}
        </div>
      </Section>

      {fw.values_assessment?.enabled && (
        <Section title="Values" subtitle={`${fw.values_assessment.values.length} values · contributes ${fw.final_rating_composition.values_weight_pct}% to final rating`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {fw.values_assessment.values.map((v) => (
              <div key={v.id} className="border border-slate-200 rounded-md p-4 bg-white">
                <div className="flex items-center gap-2">
                  <Sparkles className="w-3.5 h-3.5 text-violet-600" />
                  <div className="font-display font-semibold text-slate-900">{v.name}</div>
                </div>
                {v.description && <div className="text-xs text-slate-500 mt-1.5">{v.description}</div>}
                {v.expected_behaviours?.length > 0 && (
                  <ul className="mt-2 space-y-0.5">
                    {v.expected_behaviours.map((b, i) => (
                      <li key={i} className="text-[11px] text-slate-600">· {b}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section title="Cycle phases" subtitle="Auto-rolled out when a cycle is created from this framework">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {fw.cycle_phases.map((p) => (
            <div key={p.id} className="border border-slate-200 rounded-md p-3">
              <div className="text-xs font-medium text-slate-900">{p.label}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 capitalize">{p.type.replace(/_/g, " ")}</div>
              <div className="mt-2 axia-overline">{p.default_window_days} days default</div>
            </div>
          ))}
        </div>
      </Section>

      <Section title="Composition & rules">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <KV label="Final rating" value={`${fw.final_rating_composition.objective_weight_pct}% objectives + ${fw.final_rating_composition.values_weight_pct}% values`} />
          <KV label="Weightage rule" value={fw.weightage_rules.must_sum_to_100 ? "Must sum to 100" : "Free"} />
          <KV label="Objective count" value={`${fw.weightage_rules.min_objectives}–${fw.weightage_rules.max_objectives}`} />
          <KV label="Per-objective min weight" value={`${fw.weightage_rules.per_objective_min_weight}%`} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <section className="axia-card p-6 space-y-4">
      <div>
        <h2 className="font-display text-base font-semibold text-slate-900">{title}</h2>
        {subtitle && <div className="text-xs text-slate-500 mt-0.5">{subtitle}</div>}
      </div>
      {children}
    </section>
  );
}

function KV({ label, value }) {
  return (
    <div className="border border-slate-200 rounded-md p-3 bg-white">
      <div className="axia-overline">{label}</div>
      <div className="text-sm font-medium text-slate-900 mt-1">{value}</div>
    </div>
  );
}
